import React, { useState } from 'react';
import SelectAllButton from '../../components/SelectAllButton';
import { FiCamera, FiUploadCloud } from 'react-icons/fi';
import { trainingChecklistData } from './trainingChecklistData';

export default function TrainingFormChecklist() {
  // Local state handling radio options, comment fields, and optional text notes
  const [formState, setFormState] = useState({});
  const [additionalNotes, setAdditionalNotes] = useState('');

  // Handle updates to selection columns (YES / NO / N/A)
  const handleRadioChange = (itemId, choice) => {
    setFormState((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], selection: choice },
    }));
  };

  // Handle comment input box changes dynamically
  const handleCommentChange = (itemId, text) => {
    setFormState((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], comment: text },
    }));
  };

  return (
    <div className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm font-sans select-none p-6 overflow-x-auto">
      <div className="min-w-[800px]">
        
        {/* Table Main Column Labels */}
        <div className="grid grid-cols-12 gap-4 pb-3 border-b border-gray-100 text-[10px] font-black text-slate-500 uppercase tracking-wider items-center">
          <div className="col-span-5 text-left pl-2">Check Item</div>
          <div className="col-span-3 grid grid-cols-3 text-center">
            <span>Yes</span>
            <span>No</span>
            <span>N/A</span>
          </div>
          <div className="col-span-4 text-left pl-4">Comments / Evidence (Optional)</div>
        </div>

        {/* Dynamic List Rendering */}
        <div className="mt-4 space-y-8">
          {trainingChecklistData.map((section, sIdx) => (
            <div key={sIdx} className="space-y-3">
              {/* Category Main Header Title */}
              <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-black text-blue-900 text-left pl-1">
                {section.sectionTitle}
              </h3>
              {section.items.length > 0 && <SelectAllButton section={section.sectionTitle}
                disabled={section.items.every(item => formState[item.id]?.selection === 'yes')}
                onClick={() => setFormState(previous => {
                  const next = { ...previous };
                  section.items.forEach(item => { next[item.id] = { ...previous[item.id], selection: 'yes' }; });
                  return next;
                })} />}
              </div>

              {/* Rows List wrapper */}
              <div className="space-y-2.5">
                {section.items.map((item) => {
                  const currentState = formState[item.id] || { selection: '', comment: '' };

                  return (
                    <div
                      key={item.id}
                      className="grid grid-cols-12 gap-4 items-center bg-white hover:bg-slate-50/40 p-1.5 rounded-xl transition duration-100"
                    >
                      {/* Check Item Label Description */}
                      <div className="col-span-5 flex items-start space-x-3 text-xs font-bold text-slate-800 text-left pl-2 leading-relaxed">
                        <span className="text-slate-400 font-medium w-6 flex-shrink-0">
                          {item.label}
                        </span>
                        <span className="line-clamp-2">{item.text}</span>
                      </div>

                      {/* Triple Selection Radio Toggles Column */}
                      <div className="col-span-3 grid grid-cols-3 items-center justify-center">
                        {['yes', 'no', 'na'].map((type) => (
                          <div key={type} className="flex justify-center">
                            <input
                              type="radio"
                              aria-label={`${type}: ${item.text}`}
                              name={`choice-${item.id}`}
                              checked={currentState.selection === type}
                              onChange={() => handleRadioChange(item.id, type)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500/20 cursor-pointer accent-blue-600"
                            />
                          </div>
                        ))}
                      </div>

                      {/* Text Comments Input Box and Inline Camera Button */}
                      <div className="col-span-4 flex items-center space-x-2 pl-4">
                        <input
                          type="text"
                          value={currentState.comment || ''}
                          onChange={(e) => handleCommentChange(item.id, e.target.value)}
                          placeholder="Add comments..."
                          className="w-full text-xs px-3 py-2 border border-gray-200 rounded-lg placeholder-slate-400 bg-slate-50/20 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                        />
                        <button className="p-2 border border-gray-200 rounded-lg hover:bg-slate-50 hover:border-blue-300 text-blue-600 active:bg-blue-50 transition shadow-sm flex-shrink-0">
                          <FiCamera className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Form Actions Section Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8 pt-6 border-t border-gray-100">
          {/* Left Block: Additional Notes Area */}
          <div className="space-y-2 text-left">
            <label className="block text-xs font-black text-slate-800 tracking-tight">
              Additional Notes (optional)
            </label>
            <input
              type="text"
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              placeholder="Add any additional notes..."
              className="w-full text-xs px-4 py-3 border border-gray-200 rounded-xl placeholder-slate-400 bg-slate-50/20 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
            />
          </div>

          {/* Right Block: File Drag & Drop Upload Block */}
          <div className="space-y-2 text-left">
            <label className="block text-xs font-black text-slate-800 tracking-tight">
              Upload Evidence (optional)
            </label>
            <button className="w-full flex items-center justify-center space-x-2 py-2.5 border border-dashed border-blue-200 rounded-xl bg-blue-50/20 hover:bg-blue-50/40 text-xs font-bold text-blue-700 transition shadow-sm">
              <FiUploadCloud className="w-4 h-4 text-blue-600" />
              <span>Upload files or photos</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
